import { useEffect, useRef, useState } from "react";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";

import Header from "@/components/header";
import Footer from "@/components/footer";
import { ArrowRightIcon } from "@phosphor-icons/react";
import { motion } from "framer-motion";
import { titleInitial, titleAnimate } from "@/components/animations";
import MapBackground from "@/components/map-background";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import CurrencyInput from "react-currency-input-field";
import { Textarea } from "@/components/ui/textarea";

// set protocal to https if in production
const protocol = process.env.NODE_ENV === "production" ? "https" : "http";
const baseUrl = `${protocol}://${
  process.env.NEXT_PUBLIC_DOMAIN || "localhost:3000"
}`;

// Force SSR to avoid framer-motion static generation issues
export async function getServerSideProps() {
  return { props: {} };
}

export default function Waitlist() {
  const [skuCount, setSkuCount] = useState("");
  const [monthlySalesRevenue, setMonthlySalesRevenue] = useState("");
  const [loading, setLoading] = useState(false);
  const [formSubmitted, setFormSubmitted] = useState(false);
  const [formError, setFormError] = useState(false);
  const [validationError, setValidationError] = useState("");
  const [captchaToken, setCaptchaToken] = useState(null);

  const { executeRecaptcha } = useGoogleReCaptcha();

  useEffect(() => {
    // get the skuCount and montlySalesRevenue from the URL query
    const urlParams = new URLSearchParams(window.location.search);
    const skuCountParam = urlParams.get("skuCount");
    const monthlySalesRevenueParam = urlParams.get("monthlySalesRevenue");

    if (skuCountParam) {
      setSkuCount(skuCountParam);
    }

    if (monthlySalesRevenueParam) {
      setMonthlySalesRevenue(monthlySalesRevenueParam);
    }
  }, []);

  const handleFormSubmit = async (e) => {
    e.preventDefault();

    setLoading(true);
    setFormError(false);
    setValidationError("");

    const form = e.target;
    const formData = new FormData(form);

    const rawRevenue = formData.get("revenue");
    const normalizedRevenue = rawRevenue
      ? parseFloat(rawRevenue.replace(/,/g, ""))
      : NaN;

    if (
      !rawRevenue ||
      Number.isNaN(normalizedRevenue) ||
      normalizedRevenue <= 0
    ) {
      setValidationError(
        "Please enter an Average Monthly Revenue greater than zero.",
      );
      setLoading(false);
      return;
    }

    // Execute reCAPTCHA v3
    if (!executeRecaptcha) {
      setValidationError("reCAPTCHA not available. Please try again later.");
      setLoading(false);
      return;
    }

    let captchaToken;
    try {
      captchaToken = await executeRecaptcha("contact_form");
    } catch (error) {
      console.error("reCAPTCHA execution failed:", error);
      setValidationError("reCAPTCHA verification failed. Please try again.");
      setLoading(false);
      return;
    }

    const data = {
      email: formData.get("email"),
      properties: {
        firstName: formData.get("first-name"),
        lastName: formData.get("last-name"),
        userGroup: formData.get("company"),
        platform: formData.get("platform"),
        revenue: normalizedRevenue,
        productCount: parseInt(formData.get("products")),
        note: formData.get("message"),
      },
      captchaToken,
    };

    try {
      const response = await fetch("/api/email", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify(data),
      });

      if (response.ok) {
        const { subscribed } = await response.json();

        setFormSubmitted(true);
        if (subscribed) {
          form.reset();
          setMonthlySalesRevenue("");
          setSkuCount("");
        } else {
          setFormError(true);
        }
      } else {
        alert("There was an error subscribing you to our mailing list.");
      }
    } catch (error) {
      console.error("Error submitting form:", error);
      alert("There was an error subscribing you to our mailing list.");
    } finally {
      setLoading(false);
      setCaptchaToken(null);
    }
  };

  return (
    <>
      <Header />
      <main>
        {/* <MapBackground /> */}
        <div className="relative pt-24 md:pb-8">
          <div className="container mx-auto">
            <div className="relative sm:overflow-hidden">
              <div className="relative mx-auto max-w-6xl px-6 py-6 pr-16 md:py-16 md:pr-0 xl:px-8 xl:pt-20">
                <motion.h1
                  initial={titleInitial}
                  whileInView={titleAnimate()}
                  viewport={{ once: true }}
                  className="text-balance bg-gradient-to-r from-stone-100 via-white to-stone-100 bg-clip-text pb-5 text-4xl font-extrabold leading-none tracking-tight text-transparent drop-shadow-lg md:text-center md:text-6xl lg:text-7xl"
                >
                  <span className="block">Contact Us</span>
                </motion.h1>
                <motion.p
                  initial={titleInitial}
                  whileInView={titleAnimate(0.5)}
                  viewport={{ once: true }}
                  className="mx-auto mt-3 max-w-prose text-balance text-xl text-emerald-300/75 md:mt-6 md:text-center"
                >
                  Get in touch with our sales team. We'd love to hear more about
                  your business and how we can help.
                </motion.p>
              </div>
            </div>
          </div>
        </div>
        <div className="container mx-auto my-6 md:pb-40">
          <div className="relative grid grid-cols-1 items-center justify-start gap-4 md:grid-cols-4 md:gap-6">
            <div className="grid items-start gap-4 rounded-3xl border border-dotted border-emerald-400/20 bg-stone-950/80 p-6 md:col-span-2 md:col-start-2! md:gap-6 md:p-12">
              {formSubmitted ? (
                <div className="text-center">
                  <h2 className="text-3xl font-bold text-emerald-300">
                    Thanks for reaching out!
                  </h2>
                  <p className="mt-4 text-lg text-emerald-300">
                    We'll be in touch soon.
                  </p>
                </div>
              ) : (
                <form onSubmit={handleFormSubmit}>
                  <div className="grid grid-cols-1 gap-y-6 md:grid-cols-2 md:gap-x-8">
                    <div className="relative">
                      <Label htmlFor="first-name">First name</Label>
                      <div className="mt-1">
                        <Input
                          type="text"
                          name="first-name"
                          id="first-name"
                          autoComplete="given-name"
                          rules="string|required"
                        />
                      </div>
                    </div>
                    <div className="relative">
                      <Label htmlFor="last-name">Last name</Label>
                      <div className="mt-1">
                        <Input
                          type="text"
                          name="last-name"
                          id="last-name"
                          autoComplete="family-name"
                          rules="string|required"
                        />
                      </div>
                    </div>
                    <div className="relative md:col-span-2">
                      <Label htmlFor="email">Email</Label>
                      <div className="mt-1">
                        <Input
                          id="email"
                          name="email"
                          type="email"
                          autoComplete="email"
                          rules="email|required"
                        />
                      </div>
                    </div>
                    <div className="relative md:col-span-2">
                      <Label htmlFor="company">Website</Label>
                      <div className="mt-1">
                        <Input
                          type="text"
                          name="company"
                          id="company"
                          autoComplete="organization"
                          rules="string|required"
                        />
                      </div>
                    </div>

                    <div className="relative md:col-span-2">
                      <Label htmlFor="platform">Ecommerce Platform</Label>
                      <div className="mt-1">
                        <Select
                          id="platform"
                          name="platform"
                          className="w-full"
                          rules="required"
                        >
                          <SelectTrigger className="w-full">
                            <SelectValue placeholder="Select..." />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="Shopify">Shopify</SelectItem>
                            <SelectItem value="WooCommerce">
                              WooCommerce
                            </SelectItem>
                            <SelectItem value="Maropost">
                              Maropost (formerly Neto)
                            </SelectItem>
                            <SelectItem value="Magento">Magento</SelectItem>
                            <SelectItem value="BigCommerce">
                              BigCommerce
                            </SelectItem>
                            <SelectItem value="Ebay">Ebay</SelectItem>
                            <SelectItem value="Amazon">Amazon</SelectItem>
                            <SelectItem value="Wix">Wix</SelectItem>
                            <SelectItem value="Framer / Webflow">
                              Framer / Webflow
                            </SelectItem>
                            <SelectItem value="Squarespace">
                              Squarespace
                            </SelectItem>
                            <SelectItem value="Other">
                              Other / Custom
                            </SelectItem>
                          </SelectContent>
                        </Select>
                      </div>
                    </div>
                    <div className="relative col-span-1">
                      <Label htmlFor="revenue">Average Monthly Revenue</Label>
                      <div className="mt-1">
                        <CurrencyInput
                          name="revenue"
                          id="revenue"
                          rules="required"
                          value={monthlySalesRevenue}
                          onValueChange={(value) =>
                            setMonthlySalesRevenue(value || "")
                          }
                          min={1}
                          allowNegativeValue={false}
                          className="flex h-10 w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background file:border-0 file:bg-transparent file:text-sm file:font-medium placeholder:text-muted-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50"
                        />
                      </div>
                    </div>
                    <div className="relative col-span-1">
                      <Label htmlFor="products">Number of Products</Label>
                      <div className="mt-1">
                        <Input
                          name="products"
                          id="products"
                          rules="required"
                          type="number"
                          value={skuCount}
                          onChange={(e) => setSkuCount(e.target.value)}
                        />
                      </div>
                    </div>
                    <div className="relative md:col-span-2">
                      <Label htmlFor="message">How can we help?</Label>
                      <div className="mt-1">
                        <Textarea
                          name="message"
                          id="message"
                          rules="required"
                        />
                      </div>
                    </div>

                    {validationError ? (
                      <div className="md:col-span-2">
                        <p className="text-sm text-red-400">
                          {validationError}
                        </p>
                      </div>
                    ) : null}

                    {formError ? (
                      <div className="md:col-span-2">
                        <p className="text-sm text-red-400">
                          There was an issue submitting your enquiry. Please try
                          again.
                        </p>
                      </div>
                    ) : null}

                    <div className="md:col-span-2">
                      <p className="text-center text-xs leading-5 text-stone-400">
                        This site is protected by reCAPTCHA and the Google{" "}
                        <a
                          href="https://policies.google.com/privacy"
                          target="_blank"
                          rel="noreferrer"
                          className="text-emerald-300 transition-colors hover:text-emerald-200"
                        >
                          Privacy Policy
                        </a>{" "}
                        and{" "}
                        <a
                          href="https://policies.google.com/terms"
                          target="_blank"
                          rel="noreferrer"
                          className="text-emerald-300 transition-colors hover:text-emerald-200"
                        >
                          Terms of Service
                        </a>{" "}
                        apply.
                      </p>
                    </div>

                    <div className="md:col-span-2">
                      <Button
                        size="lg"
                        type="submit"
                        className="w-full"
                        disabled={loading}
                      >
                        <span>Send enquiry</span>
                        <ArrowRightIcon />
                      </Button>
                    </div>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>
      <Footer />
    </>
  );
}
